import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EMPTY, Subject, catchError, concatMap, map, tap } from 'rxjs';
import { AccountService } from '../account.service';
import { SectionTitleComponent } from '../../components/section-title/section-title.component';
import {
  SegmentedFilterControlComponent,
  SegmentedFilterItem,
} from '../../components/segmented-filter-control/segmented-filter-control.component';
import {
  BENACHRICHTIGUNGS_ARTEN,
  BENACHRICHTIGUNGS_KANAELE,
  Benachrichtigungen,
  BenachrichtigungsArt,
  BenachrichtigungsKanal,
} from '../../models/benachrichtigung.model';

const GESPEICHERT_ANZEIGE_MS = 1600;
const I18N = 'account.benachrichtigungen';

@Component({
  selector: 'app-benachrichtigungen-karte',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, MatIcon, SectionTitleComponent, SegmentedFilterControlComponent],
  templateUrl: './benachrichtigungen-karte.component.html',
  styleUrl: './benachrichtigungen-karte.component.scss',
})
export class BenachrichtigungenKarteComponent {
  private readonly accountService = inject(AccountService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly arten = BENACHRICHTIGUNGS_ARTEN;
  protected readonly kanaele = BENACHRICHTIGUNGS_KANAELE;
  protected readonly titelId = 'benachrichtigungen-titel';

  protected readonly stand = signal<Benachrichtigungen | null>(null);
  protected readonly laedt = signal(true);
  protected readonly ladefehler = signal(false);
  protected readonly gespeichert = signal(false);

  private bestaetigt: Benachrichtigungen | null = null;
  private ausstehend = 0;
  private readonly speicherWarteschlange = new Subject<Benachrichtigungen>();
  private gespeichertTimer?: ReturnType<typeof setTimeout>;

  protected readonly kanalItems = toSignal(
    this.translate.stream(BENACHRICHTIGUNGS_KANAELE.map((kanal) => `${I18N}.kanal.${kanal}`)).pipe(
      map((texte: Record<string, string>) =>
        BENACHRICHTIGUNGS_KANAELE.map<SegmentedFilterItem>((kanal) => ({
          id: kanal,
          label: texte[`${I18N}.kanal.${kanal}`],
        })),
      ),
    ),
    { initialValue: [] },
  );

  constructor() {
    this.destroyRef.onDestroy(() => clearTimeout(this.gespeichertTimer));
    this.speicherWarteschlange
      .pipe(
        concatMap((neu) =>
          this.accountService.putBenachrichtigungen(neu).pipe(
            tap({
              next: (antwort) => this.gespeichertBestaetigt(antwort),
              error: () => this.speichernFehlgeschlagen(),
            }),
            catchError(() => EMPTY),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.laden();
  }

  protected laden(): void {
    this.laedt.set(true);
    this.ladefehler.set(false);
    this.accountService
      .getBenachrichtigungen()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (antwort) => {
          this.bestaetigt = antwort;
          this.stand.set(antwort);
          this.laedt.set(false);
        },
        error: () => {
          this.laedt.set(false);
          this.ladefehler.set(true);
        },
      });
  }

  protected bedienbar(): boolean {
    return !this.laedt() && !this.ladefehler();
  }

  protected waehle(art: BenachrichtigungsArt, kanal: string | null): void {
    const aktuell = this.stand();
    if (!aktuell || !this.bedienbar() || !kanal) return;
    this.speichere({ ...aktuell, [art]: kanal as BenachrichtigungsKanal });
  }

  protected alleAuf(kanal: BenachrichtigungsKanal): void {
    if (!this.bedienbar()) return;
    this.speichere(Object.fromEntries(BENACHRICHTIGUNGS_ARTEN.map((art) => [art, kanal])) as Benachrichtigungen);
  }

  private speichere(neu: Benachrichtigungen): void {
    this.stand.set(neu);
    this.ausstehend++;
    this.speicherWarteschlange.next(neu);
  }

  private gespeichertBestaetigt(antwort: Benachrichtigungen): void {
    this.ausstehend--;
    this.bestaetigt = antwort;
    if (this.ausstehend === 0) this.stand.set(antwort);
    this.zeigeGespeichert();
  }

  private speichernFehlgeschlagen(): void {
    this.ausstehend--;
    if (this.ausstehend === 0) this.stand.set(this.bestaetigt);
    clearTimeout(this.gespeichertTimer);
    this.gespeichert.set(false);
    this.snackBar.open(this.translate.instant(`${I18N}.speicherfehler`), undefined, {
      panelClass: 'snackbar-error',
    });
  }

  private zeigeGespeichert(): void {
    clearTimeout(this.gespeichertTimer);
    this.gespeichert.set(true);
    this.gespeichertTimer = setTimeout(() => this.gespeichert.set(false), GESPEICHERT_ANZEIGE_MS);
  }
}
